import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Refresh as RefreshIcon,
  School as CoachIcon,
  Business as ClubIcon,
  AccountTree as InterclubIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { REGISTRATION_BASE_URL } from '../config/environment';

const ROLE_LABELS = {
  coach: { label: 'Coach', icon: <CoachIcon fontSize="small" />, color: 'warning' },
  club_admin: { label: 'Club Admin', icon: <ClubIcon fontSize="small" />, color: 'info' },
  interclub_admin: { label: 'Interclub Admin', icon: <InterclubIcon fontSize="small" />, color: 'success' },
};

export default function InterclubRoleApprovals({ interclubId }) {
  const { getIdToken } = useFirebaseAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadPendingApprovals = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);

      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      // Filter by interclubId
      const response = await fetch(
        `${REGISTRATION_BASE_URL}/role-approvals/pending?interclubId=${interclubId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to load pending approvals');
      }

      const data = await response.json();
      // Filter to only show approvals for this interclub
      const filteredApprovals = (data.approvals || []).filter(
        (a) => a.interclubId === interclubId
      );
      setPendingApprovals(filteredApprovals);
    } catch (err) {
      console.error('Failed to load pending approvals:', err);
      setError(err.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [interclubId, getIdToken]);

  const loadApprovalHistory = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      const token = await getIdToken(true);
      if (!token) return;

      const response = await fetch(
        `${REGISTRATION_BASE_URL}/role-approvals/history?interclubId=${interclubId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter to only show history for this interclub
        const filteredHistory = (data.approvals || []).filter(
          (a) => a.interclubId === interclubId
        );
        setApprovalHistory(filteredHistory);
      }
    } catch (err) {
      console.error('Failed to load approval history:', err);
    }
  }, [interclubId, getIdToken]);

  useEffect(() => {
    loadPendingApprovals();
    loadApprovalHistory();
  }, [loadPendingApprovals, loadApprovalHistory]);

  const handleApprove = async (approval) => {
    setActionLoading((prev) => ({ ...prev, [approval.id]: true }));
    setError(null);

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const response = await fetch(
        `${REGISTRATION_BASE_URL}/role-approvals/${approval.id}/approve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to approve request');
      }

      setSuccess(
        `${approval.userName}'s ${ROLE_LABELS[approval.requestedRole]?.label || approval.requestedRole} role approved!`
      );
      setTimeout(() => setSuccess(null), 3000);

      await Promise.all([loadPendingApprovals(), loadApprovalHistory()]);
    } catch (err) {
      console.error('Approval failed:', err);
      setError(err.message || 'Failed to approve request');
    } finally {
      setActionLoading((prev) => ({ ...prev, [approval.id]: false }));
    }
  };

  const handleRejectClick = (approval) => {
    setRejectTarget(approval);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;

    setActionLoading((prev) => ({ ...prev, [rejectTarget.id]: true }));
    setError(null);

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const response = await fetch(
        `${REGISTRATION_BASE_URL}/role-approvals/${rejectTarget.id}/reject`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: rejectReason || 'No reason provided' }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reject request');
      }

      setSuccess(`${rejectTarget.userName}'s request rejected.`);
      setTimeout(() => setSuccess(null), 3000);

      setRejectDialogOpen(false);
      setRejectTarget(null);
      setRejectReason('');

      await Promise.all([loadPendingApprovals(), loadApprovalHistory()]);
    } catch (err) {
      console.error('Rejection failed:', err);
      setError(err.message || 'Failed to reject request');
    } finally {
      setActionLoading((prev) => ({ ...prev, [rejectTarget?.id]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          ✅ Role Approvals
        </Typography>
        <IconButton onClick={() => { loadPendingApprovals(); loadApprovalHistory(); }} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`Pending (${pendingApprovals.length})`} />
        <Tab label="History" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        pendingApprovals.length === 0 ? (
          <Alert severity="info">No pending role requests for your interclub.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Requested Role</TableCell>
                  <TableCell>Club</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingApprovals.map((approval) => {
                  const roleInfo = ROLE_LABELS[approval.requestedRole] || {
                    label: approval.requestedRole,
                    color: 'default',
                  };
                  return (
                    <TableRow key={approval.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {approval.userName || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>{approval.userEmail}</TableCell>
                      <TableCell>
                        <Chip
                          icon={roleInfo.icon}
                          label={roleInfo.label}
                          size="small"
                          color={roleInfo.color}
                        />
                      </TableCell>
                      <TableCell>{approval.clubName || '—'}</TableCell>
                      <TableCell>{formatDate(approval.requestedAt)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="Approve">
                            <IconButton
                              color="success"
                              onClick={() => handleApprove(approval)}
                              disabled={actionLoading[approval.id]}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              color="error"
                              onClick={() => handleRejectClick(approval)}
                              disabled={actionLoading[approval.id]}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : approvalHistory.length === 0 ? (
        <Alert severity="info">No approval history for your interclub.</Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Processed</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvalHistory.map((item) => {
                const roleInfo = ROLE_LABELS[item.requestedRole] || {
                  label: item.requestedRole,
                  color: 'default',
                };
                return (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {item.userName || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={roleInfo.icon}
                        label={roleInfo.label}
                        size="small"
                        color={roleInfo.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        color={item.status === 'approved' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.processedAt)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {item.rejectionReason || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Role Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Provide a reason for rejecting {rejectTarget?.userName}'s request for{' '}
            {ROLE_LABELS[rejectTarget?.requestedRole]?.label || rejectTarget?.requestedRole} role.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={actionLoading[rejectTarget?.id]}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
